import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Get environment variables
const API_URL = import.meta.env.VITE_APP_APISERVER;
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000;
const cookiePrefix = import.meta.env.VITE_COOKIE_PREFIX

// Create axios instance with default config
export const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  withCredentials: true, // Enable sending cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track ongoing refresh to prevent multiple simultaneous refresh calls
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

// Process queued requests after successful refresh
const processQueue = (error: any = null) => {
  refreshQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  refreshQueue = [];
};

// Request interceptor - Add X-Organization-Id header if org_id exists
axiosInstance.interceptors.request.use(
  (config) => {
    // Add organization ID header if available
    const orgId = localStorage.getItem(`${cookiePrefix}_org_id`);
    if (orgId) {
      config.headers['X-Organization-Id'] = orgId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors with token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if this is the refresh-token endpoint itself - don't retry it
    const isRefreshTokenRequest = originalRequest.url?.includes('/auth/refresh-token');

    // If refresh-token endpoint returns 401, session is invalid - clear state and redirect
    if (error.response?.status === 401 && isRefreshTokenRequest) {
      console.error('Refresh token is invalid or expired');

      // Clear auth state
      localStorage.removeItem(`${cookiePrefix}_current_org`);
      localStorage.removeItem(`${cookiePrefix}_org_id`);

      // Only redirect to login from authenticated sections of the app.
      // Public pages (/, /about, /industries/*, etc.) must never redirect to login —
      // this prevents crawlers and PageSpeed from being bounced off the marketing site.
      const path = window.location.pathname;
      const isAuthenticatedSection = path.startsWith('/client/') || path.startsWith('/dashboard/');
      const isLoginPage = path.includes('/client/login');
      if (isAuthenticatedSection && !isLoginPage) {
        window.location.href = '/client/login';
      }

      return Promise.reject(new Error('Session expired. Please login again.'));
    }

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshTokenRequest) {
      // Prevent infinite retry loop
      originalRequest._retry = true;

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then(() => axiosInstance(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        // Call refresh endpoint - refresh_token cookie is sent automatically
        await axiosInstance.post('/api/v1/auth/refresh-token');

        // Refresh successful, process queued requests
        processQueue();
        isRefreshing = false;

        // Retry the original request
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear state and redirect to login
        console.error('Token refresh failed:', refreshError);

        // Create a custom error for better handling
        const authError = new Error('Session expired. Please login again.');

        // Reject all queued requests with the auth error
        processQueue(authError);
        isRefreshing = false;

        // Clear any auth-related localStorage items
        localStorage.removeItem(`${cookiePrefix}_current_org`);
        localStorage.removeItem(`${cookiePrefix}_org_id`);

        // Only redirect to login from authenticated sections of the app.
        // Public pages must never be redirected — same guard as the refresh-token handler above.
        const path = window.location.pathname;
        const isAuthenticatedSection = path.startsWith('/client/') || path.startsWith('/dashboard/');
        const isLoginPage = path.includes('/client/login');
        if (isAuthenticatedSection && !isLoginPage) {
          // Use setTimeout to ensure promise rejection happens before redirect
          setTimeout(() => {
            window.location.href = '/client/login';
          }, 100);
        }

        // Reject with a clear auth error
        return Promise.reject(authError);
      }
    }

    return Promise.reject(error);
  }
);